/* eslint-disable @typescript-eslint/adjacent-overload-signatures */
/* eslint-disable @typescript-eslint/member-ordering */
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";
import { PatientEhrOrderModel, PatientModel, PatientOrderResponseModel } from "../../models/patient.model";
import * as _ from "lodash";
import { defaultSelectOptions } from "src/app/components/default-options";
import { RecipeBase } from "src/app/models/recipe.model";
import { PatientService } from "src/app/services/patient.service";
import { FeedPatientListItem } from "src/app/pages/shared/administer-feed/feed-patient/list-item";
import { assertExhaustive, ScanLogWorkflow } from "src/app/app.enums";
import { IAdditive } from "../../models/additive.model";
import { displayDate } from "../../app.util";
import { Product } from "src/app/models/product.model";
import { MilkBottleModel } from "src/app/models/milk.model";
import {
	getStandingStatus,
	getOrderDetails,
	isOrderCalorieMatch,
	validateContentProducts,
} from "../../utils/orders.util";

@Component({
	selector: "app-order-match",
	templateUrl: "./order-match.component.html",
	styleUrls: ["./order-match.component.scss"],
})
export class OrderMatchComponent implements OnChanges {
	_patient: PatientModel;
	_ehrOrders: PatientEhrOrderModel[];
	_ordersObject: PatientOrderResponseModel

	@Input() allowOrderValidation = false;
	@Input() workflow: ScanLogWorkflow;
	@Output() selectedOrderChanged = new EventEmitter<PatientEhrOrderModel>();
	@Output() validationResults2Change = new EventEmitter<
		IOrderValidationResult2[]
	>();

	displayDate = displayDate;
	getStandingStatus = getStandingStatus;
	getOrderDetails = getOrderDetails;

	_selectedOrder: PatientEhrOrderModel;
	_validationResults: IOrderValidationResult[];
	_validationResults2: IOrderValidationResult2[];

	defaultSelectOptions = defaultSelectOptions;
	selectedItem = null;
	selectedOrderInstructions: string;

	constructor(public patientService: PatientService) {}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		// handling async with getters and setters is weird, so I'm using ngOnChanges instead to detect a change to
		// the selected patient
		if (changes.patient) {
			await this.handlePatientChanged(changes.patient.currentValue);
		}
	}


	get isValidOrderMatch() {
		return this.validationResults2?.every((v) => v.isValid);
	}

	get anyOrderFulfillmentIsValid() {
		return this.validationResults2.some((v) => v.anyOrderFulfillmentValid);
	}

	get hasActiveNPOOrder() {
		return this._ordersObject.hasActiveNPOOrder;
	}

	get caloricDensityDoesNotMatchTheOrder() {
		return !this.validationResults2?.every((v) => v.caloriesMatch);
	}

	get selectedOrder() {
		return this._selectedOrder;
	}

	set selectedOrder(value: PatientEhrOrderModel) {
		this._selectedOrder = value;
		this._validationResults = null;
		this.selectedOrderChanged.emit(this._selectedOrder);
	}

	get validationResults() {
		return this._validationResults;
	}

	get patient() {
		return this._patient;
	}

	@Input()
	set patient(value: PatientModel) {
		this._patient = value;
	}

	get ehrOrders() {
		return this._ehrOrders;
	}

	set ehrOrders(value: PatientEhrOrderModel[]) {
		this._ehrOrders = value;
	}

	get ordersObject() {
		return this._ordersObject;
	}

	set ordersObject(value: PatientOrderResponseModel) {
		this._ordersObject = value;
	}

	async handlePatientChanged(patient: PatientModel) {
		this.ehrOrders = [];

		if (patient) {
			this.ordersObject = await this.patientService.getPatientEhrOrders(
				patient.id,
				this.workflow
			);

			this.ehrOrders = this.ordersObject.orders;
			this.setDefaultOrder();
		}
	}

	get validationResults2() {
		return this._validationResults2;
	}

	set validationResults2(value: IOrderValidationResult2[]) {
		this._validationResults2 = value;
		this.validationResults2Change.emit(this.validationResults2);
	}

	/** preselect the first (latest) order, sorted by the backend
	 */
	setDefaultOrder() {
		if (this.ehrOrders?.length > 0) {
			this.selectedItem = 0;
			this.selectedOrder = this.ehrOrders[0];
			this.selectedOrderInstructions =
				this.ehrOrders[0].instructions || "None";
			this.selectedOrderChanged.emit(this.selectedOrder);
		}
	}

	selectOrder(index: number) {
		this.selectedItem = index;
		this.selectedOrder = this.ehrOrders[index];
		this.selectedOrderInstructions =
			this.ehrOrders[index].instructions || "None";
		this.selectedOrderChanged.emit(this.selectedOrder);
	}

	validateOrderMatchesFeedPatientListItems(
		feedPatientListItems: FeedPatientListItem[]
	) {
		this.validationResults2 =
			this.validateSelectedOrderMatchesFeedPatientListItems(
				this.selectedOrder,
				feedPatientListItems
			);
	}

	validateOrderMatchesRecipeCalculation(params: {
		calorieDensity: number;
		additives: IAdditive[];
		base: RecipeBase;
	}) {
		this.validationResults2 = this._validateOrderMatchesRecipeCalculation(
			this.selectedOrder,
			params.calorieDensity,
			params.additives,
			params.base
		);
	}

	validateSelectedOrderMatchesManualPrep(params: {
		calorieDensity: number;
		products: { productId: Product["id"]; productName: Product["name"] }[];
		recipeBase: RecipeBase;
	}) {
		if (!params.calorieDensity || params.products.length === 0) {
			console.warn(
				// biome-ignore lint/nursery/noSecrets: <explanation>
				"validateSelectedOrderMatchesManualPrep: params are null"
			);
			return;
		}

		const selectedOrder = this.selectedOrder;

		// include the recipe base in products so that's it's included in the validation
		const products = params.products
			.filter((a) => a.productId)
			.map((a) => ({
				productId: a.productId,
				productName: a.productName,
			}))
			.concat({
				productId: params.recipeBase.baseProductId,
				productName: params.recipeBase.baseName,
			});

		const resultsByOrderFulfillment =
			this.validateSelectedOrderMatchesProducts(
				this.selectedOrder,
				products
			);
		const anyOrderFulfillmentValid = resultsByOrderFulfillment.some(
			(orderFulfillment) => orderFulfillment.isValid
		);
		const caloriesMatch = isOrderCalorieMatch(selectedOrder, params.calorieDensity);

		this.validationResults2 = [
			{
				milkBottleId: null,
				isValid: anyOrderFulfillmentValid && caloriesMatch,
				anyOrderFulfillmentValid,
				caloriesMatch,
				validationResultsByOrderFulfillment: resultsByOrderFulfillment,
			},
		];
	}

	validateSelectedOrderMatchesFeedPatientListItems(
		selectedPatientEhrOrder: PatientEhrOrderModel,
		feedPatientListItems: FeedPatientListItem[]
	): IOrderValidationResult2[] {
		/*
			If validation happens when the patient doesn't have any active orders,
			should return all warnings.
		 */
		if (!selectedPatientEhrOrder) {
			return feedPatientListItems.map((item) => ({
				milkBottleId:
					item.type === "milkbottle" ? item.milkBottle.id : null,
				key: item.key,
				isValid: false,
				anyOrderFulfillmentValid: false,
				caloriesMatch: false,
				validationResultsByOrderFulfillment: [],
			}));
		}

		const resultsByFeedPatientListItem: IOrderValidationResult2[] =
			feedPatientListItems.map((item) => {
				// pull products and calories out of the FeedPatientListItem
				let products: Array<{
					productId: Product["id"];
					productName: Product["name"];
				}>;
				let calorie: number;

				switch (item.type) {
					case "milkbottle":
						products = item.milkBottle.contents.map((c) => ({
							productId: c.contentProductId,
							productName: c.contentProductName,
						}));
						calorie = item.milkBottle.calorieObject?.caloriesOZ ?? 0;
						break;
					case "product":
						products = [
							{
								productId: item.product.id,
								productName: item.product.name,
							},
						];
						calorie = item.product.calorieObject?.caloriesOZ ?? item.product.calorieDensity;
						break;
					default:
						assertExhaustive(item);
				}

				const resultsByOrderFulfillment =
					this.validateSelectedOrderMatchesProducts(
						selectedPatientEhrOrder,
						products
					);
				const anyOrderFulfillmentValid = resultsByOrderFulfillment.some(
					(orderFulfillment) => orderFulfillment.isValid
				);
				const caloriesMatch = isOrderCalorieMatch(
					selectedPatientEhrOrder,
					calorie,
				);

				return {
					milkBottleId:
						item.type === "milkbottle" ? item.milkBottle.id : null,
					key: item.key,
					isValid: anyOrderFulfillmentValid && caloriesMatch,
					anyOrderFulfillmentValid,
					caloriesMatch,
					validationResultsByOrderFulfillment:
						resultsByOrderFulfillment,
				};
			});

		return resultsByFeedPatientListItem;
	}

	/**
	 * Generic validation routine. Validates against products: Array<{productId: string; productName: string}>
	 *
	 * Uses the same order-matching rules as the URC and the recipe calculators
	 * (validateContentProducts). Matching by productId alone rejected a bottle made with Similac or
	 * Enfamil Sterile Water against an order whose base is the generic Water; now any water stands
	 * in for the order's water (AEP-2306). EBM or DM stands in for the order's human milk only when
	 * the order's base allows that milk, so DM still fails an EBM-only order (AEP-2452).
	 */
	private validateSelectedOrderMatchesProducts(
		selectedEhrOrder: PatientEhrOrderModel,
		products: Array<{
			productId?: Product["id"];
			productName: Product["name"];
		}>
	) {
		// A bottle can hold the same product more than once (e.g. combined bottles); list each once.
		const bottleProducts = _.chain(products)
			.filter((contentItem) => contentItem.productId != null)
			.uniqBy((contentItem) => contentItem.productId)
			.map((contentItem) => ({
				productId: contentItem.productId,
				productName: contentItem.productName,
			}))
			.value();

		const resultsByOrderFulfillment = selectedEhrOrder.fulfillments.map(
			(ehrOrderFulfillment) =>
				validateContentProducts(
					bottleProducts,
					ehrOrderFulfillment,
					selectedEhrOrder.base
				)
		);

		return resultsByOrderFulfillment;
	}

	private _validateOrderMatchesRecipeCalculation(
		selectedOrder: PatientEhrOrderModel,
		calorieDensity: number,
		additives: IAdditive[],
		recipeBase: RecipeBase
	): IOrderValidationResult2[] {
		if (!selectedOrder || !calorieDensity || !additives.length) {
			console.warn("validateRecipe: params are null");
			console.table({
				selectedOrder,
				calorieDensity,
				additives,
			});
			return;
		}

		// include the recipe base in products so that's it's included in the validation
		const products = additives
			.filter((a) => a.productId)
			.map((a) => ({
				productId: a.productId,
				productName: a.productName,
			}))
			.concat({
				productId: recipeBase.baseProductId,
				productName: recipeBase.baseName,
			});

		const resultsByOrderFulfillment =
			this.validateSelectedOrderMatchesProducts(
				this.selectedOrder,
				products
			);
		const anyOrderFulfillmentValid = resultsByOrderFulfillment.some(
			(orderFulfillment) => orderFulfillment.isValid
		);
		const caloriesMatch = isOrderCalorieMatch(selectedOrder, calorieDensity);

		return [
			{
				milkBottleId: null,
				isValid: anyOrderFulfillmentValid && caloriesMatch,
				anyOrderFulfillmentValid,
				caloriesMatch,
				validationResultsByOrderFulfillment: resultsByOrderFulfillment,
			},
		];
	}

	/**
	 * Method signature must match https://angular.io/api/core/TrackByFunction
	 *
	 * Without trackBy, Angular will re-render the entire list,
	 * rather than only refreshing and updating as needed. This causes
	 * jumping/snapping issues.
	 */
	trackByOrder(index, ehrOrder) {
		return (ehrOrder as PatientEhrOrderModel).id;
	}
}

export const isMilkBottleOrderMatch = (
	milkBottleId: string,
	results: IOrderValidationResult2[]
): boolean => {
	if (!results) {
		return;
	}

	const result = results.find((v) => v.milkBottleId === milkBottleId);
	if (!result) {
		console.warn(
			`order-match.component: milkBottle not found: ${milkBottleId}`
		);
	}
	return result.isValid;
};

/**
 * @deprecated use {@link isScannedItemOrderMatch} in {@link orders.utils.ts} instead
 */
export const isScannedItemOrderMatch = (
	scannedItemKey: string,
	validationResults: IOrderValidationResult2[]
): boolean => {
	if (!validationResults) {
		return;
	}

	const result = validationResults.find((v) => v.key === scannedItemKey);
	if (!result) {
		console.warn(
			`order-match.component: scannedItem not found: scannedItemKey = ${scannedItemKey}`
		);
	}
	return result.isValid;
};

export interface IOrderValidationResult {
	milkBottleId?: string;
	contentItemsNotInOrder: Array<{
		productId: Product["id"];
		productName: Product["name"];
	}>;
	baseOrderedIsPresent: boolean;
	orderNonBaseProductsNotInBottleOrRecipeCalculation: Array<{
		productId: Product["id"];
		productName: Product["name"];
	}>;
	caloriesMatched: boolean;
	isValid: boolean;
}

export interface IOrderValidationResult2 {
	milkBottleId?: MilkBottleModel["id"];
	key?: string; // unique id using nano id
	isValid: boolean;
	caloriesMatch: boolean;
	anyOrderFulfillmentValid: boolean;
	validationResultsByOrderFulfillment: Array<{
		ehrOrderFulfillmentId: string;
		milkBottleContentItemsNotInOrderFulfillment: {
			productId: Product["id"];
			productName: Product["name"];
		}[];
		baseOrderedIsPresent: boolean;
		orderFulfillmentNonBaseProductsNotInBottle: {
			productId: Product["id"];
			productName: Product["name"];
		}[];
		isValid: boolean;
	}>;
}
